// Empire Apps Chassis · elder · big-tap
// Web components for elder-first UIs.
// Every component defaults to 88pt tap, 22pt text, and labels-always-visible.
// Icons are paired with words (icon alone is never sufficient).

import { ensureMinTapSize, screenReaderAnnounce, getRecommendedFontPx } from "./a11y.ts";
import type { FontScale } from "./types.ts";

const STYLE_TAG_ID = "empire-elder-styles";

function ensureBaseStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = `
    :root {
      --empire-elder-font-scale: 1.0;
      --empire-elder-bg: #faf5e8;
      --empire-elder-fg: #1c1a16;
      --empire-elder-accent: #b85410;
      --empire-elder-danger: #8e1d1d;
      --empire-elder-focus: #f1b65b;
      --empire-elder-min-tap-px: 117px;
      --empire-elder-base-px: 22px;
      --empire-elder-radius: 14px;
      --empire-elder-shadow: 0 4px 16px rgba(0,0,0,0.08);
    }
    empire-big-button, empire-back-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      min-width: var(--empire-elder-min-tap-px);
      min-height: var(--empire-elder-min-tap-px);
      padding: 0.875rem 1.5rem;
      font-size: calc(var(--empire-elder-base-px) * var(--empire-elder-font-scale));
      font-weight: 600;
      line-height: 1.4;
      border-radius: var(--empire-elder-radius);
      background: var(--empire-elder-accent);
      color: #ffffff;
      cursor: pointer;
      user-select: none;
      transition: transform 60ms ease-out, box-shadow 60ms ease-out;
      box-shadow: var(--empire-elder-shadow);
    }
    empire-big-button:focus-visible, empire-back-button:focus-visible {
      outline: 4px solid var(--empire-elder-focus);
      outline-offset: 4px;
    }
    empire-big-button[variant="ghost"] {
      background: transparent;
      color: var(--empire-elder-fg);
      border: 2px solid var(--empire-elder-fg);
      box-shadow: none;
    }
    empire-big-button[variant="danger"] {
      background: var(--empire-elder-danger);
    }
    empire-big-button[aria-pressed="true"] {
      transform: scale(0.98);
    }
    empire-big-text {
      display: block;
      font-size: calc(var(--empire-elder-base-px) * var(--empire-elder-font-scale));
      line-height: 1.6;
      color: var(--empire-elder-fg);
      font-family: "Mukta", "Crimson Pro", "Noto Sans Devanagari", serif;
    }
    empire-big-text[as="h1"] { font-size: calc(34px * var(--empire-elder-font-scale)); font-weight: 700; }
    empire-big-text[as="h2"] { font-size: calc(28px * var(--empire-elder-font-scale)); font-weight: 600; }
    empire-big-text[as="caption"] { font-size: calc(18px * var(--empire-elder-font-scale)); opacity: 0.78; }
    empire-big-card {
      display: block;
      padding: 1.5rem;
      background: #ffffff;
      border-radius: var(--empire-elder-radius);
      box-shadow: var(--empire-elder-shadow);
      margin: 0.75rem 0;
    }
    empire-big-card[tone="warning"] { background: #fff4d6; border-left: 6px solid var(--empire-elder-accent); }
    empire-big-card[tone="danger"]  { background: #fde0e0; border-left: 6px solid var(--empire-elder-danger); }
    empire-big-form-field {
      display: block;
      margin: 1.25rem 0;
    }
    empire-big-form-field .empire-label {
      display: block;
      font-size: calc(20px * var(--empire-elder-font-scale));
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: var(--empire-elder-fg);
    }
    empire-big-form-field input,
    empire-big-form-field textarea,
    empire-big-form-field select {
      width: 100%;
      min-height: var(--empire-elder-min-tap-px);
      padding: 0.75rem 1rem;
      font-size: calc(22px * var(--empire-elder-font-scale));
      font-family: inherit;
      border-radius: var(--empire-elder-radius);
      border: 2px solid var(--empire-elder-fg);
      background: #ffffff;
      color: var(--empire-elder-fg);
    }
    empire-big-form-field input:focus-visible,
    empire-big-form-field textarea:focus-visible,
    empire-big-form-field select:focus-visible {
      outline: 4px solid var(--empire-elder-focus);
      outline-offset: 2px;
    }
    empire-big-form-field .empire-helper {
      display: block;
      font-size: calc(18px * var(--empire-elder-font-scale));
      margin-top: 0.5rem;
      opacity: 0.78;
    }
    empire-big-form-field .empire-error {
      display: block;
      font-size: calc(18px * var(--empire-elder-font-scale));
      margin-top: 0.5rem;
      color: var(--empire-elder-danger);
      font-weight: 600;
    }
    empire-back-button {
      position: sticky;
      top: 12px;
      left: 12px;
      align-self: flex-start;
    }
  `;
  document.head.appendChild(style);
}

abstract class ElderBase extends HTMLElement {
  protected scale: FontScale = 1.0;
  connectedCallback(): void {
    ensureBaseStyles();
    if (this instanceof HTMLElement) {
      ensureMinTapSize(this, this.minPoints());
    }
    document.addEventListener("empire-font-scale-change", this.onScale);
  }
  disconnectedCallback(): void {
    document.removeEventListener("empire-font-scale-change", this.onScale);
  }
  private onScale = (ev: Event): void => {
    const detail = (ev as CustomEvent<{ scale: FontScale }>).detail;
    if (detail?.scale) {
      this.scale = detail.scale;
      this.applyScale();
    }
  };
  protected applyScale(): void {
    this.style.setProperty(
      "--empire-elder-base-px",
      `${getRecommendedFontPx(this.scale, 22)}px`,
    );
  }
  protected minPoints(): number {
    return 88;
  }
}

export class EmpireBigButton extends ElderBase {
  static get observedAttributes(): readonly string[] {
    return ["variant", "loading", "disabled"];
  }
  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute("role", "button");
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");
    this.addEventListener("click", this.handleActivate);
    this.addEventListener("keydown", this.handleKey);
    const label = this.getAttribute("label") ?? this.textContent ?? "";
    if (label && !this.getAttribute("aria-label")) {
      this.setAttribute("aria-label", label.trim());
    }
  }
  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener("click", this.handleActivate);
    this.removeEventListener("keydown", this.handleKey);
  }
  private handleActivate = (ev: Event): void => {
    if (this.hasAttribute("disabled") || this.hasAttribute("loading")) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      return;
    }
    const announce = this.getAttribute("announce-on-tap");
    if (announce) screenReaderAnnounce(announce);
  };
  private handleKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      this.click();
    }
  };
}

export class EmpireBackButton extends EmpireBigButton {
  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.textContent || this.textContent.trim() === "") {
      this.textContent = "wapas jaayein";
    }
    this.setAttribute("variant", this.getAttribute("variant") ?? "ghost");
  }
}

export class EmpireBigText extends ElderBase {
  override connectedCallback(): void {
    super.connectedCallback();
    const as = this.getAttribute("as");
    if (as === "h1" || as === "h2" || as === "h3") {
      this.setAttribute("role", "heading");
      this.setAttribute("aria-level", as === "h1" ? "1" : as === "h2" ? "2" : "3");
    }
  }
}

export class EmpireBigCard extends ElderBase {
  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.hasAttribute("role")) this.setAttribute("role", "group");
  }
}

export class EmpireBigFormField extends ElderBase {
  override connectedCallback(): void {
    super.connectedCallback();
    const labelText = this.getAttribute("label") ?? "";
    const helperText = this.getAttribute("helper") ?? "";
    const errorText = this.getAttribute("error") ?? "";
    const inputId = `empire-ff-${Math.random().toString(36).slice(2, 8)}`;

    const labelEl = document.createElement("label");
    labelEl.className = "empire-label";
    labelEl.htmlFor = inputId;
    labelEl.textContent = labelText;

    const slot = this.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "input, textarea, select",
    );
    if (slot) {
      slot.id = inputId;
      slot.setAttribute("aria-describedby", `${inputId}-helper`);
    }

    this.insertBefore(labelEl, this.firstChild);
    if (helperText) {
      const helper = document.createElement("span");
      helper.className = "empire-helper";
      helper.id = `${inputId}-helper`;
      helper.textContent = helperText;
      this.appendChild(helper);
    }
    if (errorText) {
      const err = document.createElement("span");
      err.className = "empire-error";
      err.setAttribute("role", "alert");
      err.textContent = errorText;
      this.appendChild(err);
    }
  }
}

let registered = false;
export function registerElderComponents(): void {
  if (registered) return;
  if (typeof customElements === "undefined") return;
  if (!customElements.get("empire-big-button")) {
    customElements.define("empire-big-button", EmpireBigButton);
  }
  if (!customElements.get("empire-back-button")) {
    customElements.define("empire-back-button", EmpireBackButton);
  }
  if (!customElements.get("empire-big-text")) {
    customElements.define("empire-big-text", EmpireBigText);
  }
  if (!customElements.get("empire-big-card")) {
    customElements.define("empire-big-card", EmpireBigCard);
  }
  if (!customElements.get("empire-big-form-field")) {
    customElements.define("empire-big-form-field", EmpireBigFormField);
  }
  registered = true;
  ensureBaseStyles();
}
